import { defineStore } from 'pinia'
import { park } from '@/qingyu/request/requests'

// 定义园区状态存储
export const useParkStore = defineStore('park', {
  // 状态
  state: () => ({
    // 园区列表
    parkList: [],
    // 当前选中的园区
    currentPark: null,
    // 园区详情
    parkDetail: null,
    // 楼宇列表
    buildingList: [],
    // 当前选中的楼宇
    currentBuilding: null,
    // 楼层列表
    floorList: [],
    // 当前选中的楼层
    currentFloor: null,
    // 空间列表
    spaceList: [],
    // 当前选中的空间
    currentSpace: null,
    // 合同信息
    contractInfo: null,
    // 物业费用信息
    propertyFeeInfo: null,
    // 入驻信息列表
    tenancyList: [],
    // 入驻信息加载状态
    tenancyLoading: false,
    // 加载状态
    loading: false
  }),

  // 计算属性
  getters: {
    // 获取园区名称列表，用于下拉选择
    parkOptions: (state) => {
      return state.parkList.map(park => ({
        label: park.yuanqu_name,
        value: park.id
      }));
    },
    
    // 获取当前园区的楼宇选项
    buildingOptions: (state) => {
      return state.buildingList.map(building => ({
        label: building.building_name,
        value: building.id
      }));
    },
    
    // 获取当前楼宇的楼层选项
    floorOptions: (state) => {
      return state.floorList.map(floor => ({
        label: floor.floor_name,
        value: floor.id
      }));
    },
    
    // 获取当前楼层的房间选项
    roomOptions: (state) => {
      return state.spaceList.map(room => ({
        label: room.room_name,
        value: room.id,
        area: room.area
      }));
    }
  },
  
  // 动作
  actions: {
    // 获取园区列表
    async getParkList(params) {
      this.loading = true
      try {
        const response = await park.getParkList(params)
        console.log('获取园区列表响应:', response)
        // 处理API返回的数据格式，进行字段映射
        if (response && Array.isArray(response)) {
          this.parkList = response.map(item => ({
            id: item.id,
            name: item.yuanqu_name,
            address: item.address,
            coverImage: item.yuanqu_img && item.yuanqu_img.length > 0 ? item.yuanqu_img[0] : null,
            images: item.yuanqu_img || [],
            // 保留其他可能存在的字段
            ...item
          }))
        } else {
          this.parkList = []
        }
        return this.parkList
      } catch (error) {
        console.error('获取园区列表失败', error)
        return []
      } finally {
        this.loading = false
      }
    },
    
    // 获取园区详情
    async getParkDetail(parkId) {
      if (!parkId && this.currentPark) {
        parkId = this.currentPark.id
      }
      
      if (!parkId) {
        return null
      }
      
      this.loading = true
      try {
        const response = await park.getParkDetail(parkId)
        
        // 处理API返回的数据格式
        if (response) {
          this.parkDetail = {
            id: response.id,
            name: response.yuanqu_name,
            address: response.address,
            coverImage: response.yuanqu_img && response.yuanqu_img.length > 0 ? response.yuanqu_img[0] : null,
            images: response.yuanqu_img || [],
            description: response.note || '',
            contact: response.contact || '',
            contactPhone: response.phone || '',
            // 保留其他可能存在的字段
            ...response
          }
        } else {
          this.parkDetail = null
        }
        
        return this.parkDetail
      } catch (error) {
        console.error('获取园区详情失败', error)
        return null
      } finally {
        this.loading = false
      }
    },
    
    // 设置当前园区
    setCurrentPark(park) {
      this.currentPark = park
      // 清空相关状态
      this.buildingList = []
      this.currentBuilding = null
      this.floorList = []
      this.currentFloor = null
      this.spaceList = []
      this.currentSpace = null
    },
    
    // 获取楼宇列表
    async getBuildingList(parkId) {
      if (!parkId && this.currentPark) {
        parkId = this.currentPark.id;
      }
      
      if (!parkId) {
        return [];
      }
      
      this.loading = true;
      try {
        const response = await park.getBuildingList(parkId);
        if (response && Array.isArray(response)) {
          this.buildingList = response;
        } else {
          this.buildingList = [];
        }
        return this.buildingList;
      } catch (error) {
        console.error('获取楼宇列表失败', error);
        return [];
      } finally {
        this.loading = false;
      }
    },
    
    // 设置当前楼宇
    setCurrentBuilding(building) {
      this.currentBuilding = building;
      // 清空相关状态
      this.floorList = [];
      this.currentFloor = null;
      this.spaceList = [];
      this.currentSpace = null;
    },
    
    // 获取楼层列表
    async getFloorList(buildingId) {
      if (!buildingId && this.currentBuilding) {
        buildingId = this.currentBuilding.id;
      }
      
      if (!buildingId) {
        return [];
      }
      
      this.loading = true;
      try {
        const response = await park.getFloorList(buildingId);
        if (response && Array.isArray(response)) {
          this.floorList = response;
        } else {
          this.floorList = [];
        }
        return this.floorList;
      } catch (error) {
        console.error('获取楼层列表失败', error);
        return [];
      } finally {
        this.loading = false;
      }
    },
    
    // 设置当前楼层
    setCurrentFloor(floor) {
      this.currentFloor = floor;
      // 清空相关状态
      this.spaceList = [];
      this.currentSpace = null;
    },
    
    // 获取房间列表
    async getRoomList(floorId) {
      if (!floorId && this.currentFloor) {
        floorId = this.currentFloor.id;
      }
      
      if (!floorId) {
        return [];
      }
      
      this.loading = true;
      try {
        const response = await park.getRoomList(floorId);
        if (response && Array.isArray(response)) {
          this.spaceList = response;
        } else {
          this.spaceList = [];
        }
        return this.spaceList;
      } catch (error) {
        console.error('获取房间列表失败', error);
        return [];
      } finally {
        this.loading = false;
      }
    },
    
    // 设置当前空间
    setCurrentSpace(space) {
      this.currentSpace = space
    },
    

    
    // 申请入驻园区
    async applyForPark(data) {
      this.loading = true
      try {
        const response = await park.applyForPark(data)
        return response
      } catch (error) {
        console.error('申请入驻园区失败', error)
        throw error
      } finally {
        this.loading = false
      }
    },
    
    // 获取园区3D模型
    async getPark3DModel(parkId) {
      if (!parkId && this.currentPark) {
        parkId = this.currentPark.id
      }
      
      if (!parkId) {
        return null
      }
      
      this.loading = true
      try {
        const response = await park.getPark3DModel(parkId)
        return response
      } catch (error) {
        console.error('获取园区3D模型失败', error)
        return null
      } finally {
        this.loading = false
      }
    },
    
    // 获取合同信息
    async getContractInfo() {
      this.loading = true
      try {
        const response = await park.getContractInfo()
        this.contractInfo = response
        return response
      } catch (error) {
        console.error('获取合同信息失败', error)
        return null
      } finally {
        this.loading = false
      }
    },
    
    // 获取物业费用信息
    async getPropertyFeeInfo() {
      this.loading = true
      try {
        const response = await park.getPropertyFeeInfo()
        this.propertyFeeInfo = response
        return response
      } catch (error) {
        console.error('获取物业费用信息失败', error)
        return null
      } finally {
        this.loading = false
      }
    },
    
    // 获取入驻信息列表
    async getTenancyList(params = {}) {
      this.tenancyLoading = true;
      try {
        const response = await park.getTenancyList(params);
        console.log('获取入驻信息列表响应:', response);
        
        if (response && Array.isArray(response)) {
          this.tenancyList = response.map(item => ({
            roomTotalName: item.room_total_name,
            companyName: item.company_name,
            startDay: item.start_day,
            endDay: item.end_day,
            rent: item.rent,
            rentDay: item.rent_day,
            contractImages: item.contract_img || [],
            rentImages: item.rent_img || [],
            remainDays: item.remainDays,
            remainMoney: item.remainMoney,
            // 保留原始数据
            ...item
          }));
        } else {
          this.tenancyList = [];
        }
        
        return this.tenancyList;
      } catch (error) {
        console.error('获取入驻信息列表失败', error);
        return [];
      } finally {
        this.tenancyLoading = false;
      }
    },
    
    // 重置园区状态
    resetParkState() {
      this.parkList = []
      this.currentPark = null
      this.parkDetail = null
      this.buildingList = []
      this.currentBuilding = null
      this.floorList = []
      this.currentFloor = null
      this.spaceList = []
      this.currentSpace = null
      this.contractInfo = null
      this.propertyFeeInfo = null
      this.tenancyList = []
    }
  },
  
  // 持久化配置
  persist: {
    key: 'park-store',
    storage: sessionStorage,
    paths: ['currentPark', 'currentBuilding', 'currentFloor', 'currentSpace']
  }
}) 